import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import HttpClient from '@@/package/network/HttpClient';
import {type AxiosResponse} from 'axios';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import type SubscriptionResponse from '~~/package/apis/subscription/response/SubscriptionResponse';
import type CustomResponse from '~~/package/types/ResponseType';
import type PlansResponse from '~~/package/apis/subscription/response/PlanResponse';
import type {CheckoutRequest} from '~~/package/apis/subscription/request/CheckoutRequest';
import type {UpdatePaymentMethodRequest} from '~~/package/apis/subscription/request/UpdatePaymentMethodRequest';
import type {ChangeBillingCycleRequest} from '~~/package/apis/subscription/request/ChangeBillingCycleRequest';
import type {UpgradePlanRequest} from '~~/package/apis/subscription/request/UpgradePlanRequest';
import type CheckoutResponse from '~~/package/apis/subscription/response/CheckoutResponse';
import type PaymentResponse from '~~/package/apis/subscription/response/PaymentResponse';
import type PaymentHistoryResponse from '~~/package/apis/subscription/response/PaymentHistoryResponse';
import type PaymentMethodResponse from '~~/package/apis/subscription/response/PaymentMethodResponse';
import type { PageModel } from '~~/package/mapper/common/model/PageModel';
import type CreditPricingResponse from '~~/package/apis/subscription/response/CreditPricingResponse';
import type SetupIntentResponse from '~~/package/apis/subscription/response/SetupIntentResponse';
import type UpgradeQuoteResponse from '~~/package/apis/subscription/response/UpgradeQuoteResponse';

export default class SubscriptionApi {
    protected http: HttpClient;

    constructor(context: ApplicationContextInterface) {
        this.http = context.getService<HttpClient>(DependencyInjectId.HttpClient);
    }

    public getCurrentSubscription(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.get<AxiosResponse<SubscriptionResponse>>('/api/v1/subscriptions/current', undefined, true);
    }

    public getPlans(): Promise<AxiosResponse<PlansResponse>> {
        return this.http.get<AxiosResponse<PlansResponse>>('/api/v1/plans', undefined, false)
    }

    public checkout(request: CheckoutRequest): Promise<AxiosResponse<CheckoutResponse>> {
        return this.http.post<AxiosResponse<CheckoutResponse>, CheckoutRequest>('/api/v1/subscriptions/checkout', request, true);
    }

    public getPayment(paymentKey: string): Promise<AxiosResponse<PaymentResponse>> {
        return this.http.get<AxiosResponse<PaymentResponse>>(`/api/v1/payments/${paymentKey}`, undefined, true);
    }

    public getDefaultPaymentMethod(): Promise<AxiosResponse<PaymentMethodResponse | null>> {
        return this.http.get<AxiosResponse<PaymentMethodResponse | null>>('/api/v1/payment-methods/default', undefined, true);
    }

    public updatePaymentMethod(request: UpdatePaymentMethodRequest): Promise<AxiosResponse<PaymentMethodResponse>> {
        return this.http.post<AxiosResponse<PaymentMethodResponse>, UpdatePaymentMethodRequest>('/api/v1/payment-methods', request, true);
    }

    public changeBillingCycle(request: ChangeBillingCycleRequest): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.put<AxiosResponse<SubscriptionResponse>, ChangeBillingCycleRequest>('/api/v1/subscriptions/current/billing-cycle-change', request, true);
    }

    public cancelBillingCycleChange(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.delete<AxiosResponse<SubscriptionResponse>>('/api/v1/subscriptions/current/billing-cycle-change', undefined, true);
    }

    public getUpgradeQuote(): Promise<AxiosResponse<UpgradeQuoteResponse>> {
        return this.http.get<AxiosResponse<UpgradeQuoteResponse>>('/api/v1/subscriptions/current/upgrade-quote', undefined, true);
    }

    public upgradePlan(request: UpgradePlanRequest): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.post<AxiosResponse<SubscriptionResponse>, UpgradePlanRequest>('/api/v1/subscriptions/current/upgrade', request, true);
    }

    public cancelSubscription(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.post<AxiosResponse<SubscriptionResponse>, undefined>('/api/v1/subscriptions/current/cancel', undefined, true);
    }

    public cancelCancellationRequest(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.post<AxiosResponse<SubscriptionResponse>, undefined>('/api/v1/subscriptions/current/revoke-cancel', undefined, true);
    }

    public restartSubscription(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.post<AxiosResponse<SubscriptionResponse>, undefined>('/api/v1/subscriptions/restart', undefined, true);
    }

    public retryPayment(): Promise<AxiosResponse<SubscriptionResponse>> {
        return this.http.post<AxiosResponse<SubscriptionResponse>, undefined>('/api/v1/subscriptions/current/resume', undefined, true);
    }

    public getPaymentHistory(page: number, size: number): Promise<AxiosResponse<CustomResponse<PageModel<PaymentHistoryResponse>>>> {
        return this.http.get<AxiosResponse<CustomResponse<PageModel<PaymentHistoryResponse>>>>('/api/v1/payments/history', { page, size }, true);
    }

    public getCreditPricing(): Promise<AxiosResponse<CreditPricingResponse>> {
        return this.http.get<AxiosResponse<CreditPricingResponse>>('/api/v1/subscriptions/current/plan/credit-pricing', undefined, true);
    }

    public getStripeSetupIntent(): Promise<AxiosResponse<SetupIntentResponse>> {
        return this.http.post<AxiosResponse<SetupIntentResponse>, undefined>('/api/v1/payment-methods/stripe/setup-intent', undefined, true);
    }
}
